import { Button, useIdempotencyKeys, zonedInstant } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { paymentActions, RECORD_METHOD, type RecordMethod } from "../../payments";
import styles from "./orders.module.css";

/**
 * Collecting an order's payment from the console (P8-39): record money that came in outside the
 * app, prompt the customer's phone, let the order go out before it is paid, or mark a refunded
 * order unpaid.
 */

/** A payment changes the order's page, the orders list and the payments panel. */
function useRefreshOrder(orderId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
    await queryClient.invalidateQueries({ queryKey: ["orders"] });
    await queryClient.invalidateQueries({ queryKey: ["payments"] });
  };
}

function Footer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className={styles.footer}>
      <Button type="button" variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      {children}
    </div>
  );
}

/** A payment on an invoice changes its page, the invoices, payments and ageing (P8-71). */
function useRefreshInvoice(invoiceId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["invoice", invoiceId] });
    await queryClient.invalidateQueries({ queryKey: ["invoices"] });
    await queryClient.invalidateQueries({ queryKey: ["payments"] });
    await queryClient.invalidateQueries({ queryKey: ["ageing"] });
  };
}

/**
 * Money received outside the app: cash, a bank transfer, a cheque, M-Pesa sent to a number; for
 * an order (P8-39) or an issued invoice (P8-71).
 */
export function RecordPaymentDialog({
  target,
  number,
  onClose,
}: {
  target: { order_id: string } | { invoice_id: string };
  /** the order's or invoice's number, for the title */
  number: string;
  onClose: () => void;
}) {
  const forInvoice = "invoice_id" in target;
  const refreshOrder = useRefreshOrder("order_id" in target ? target.order_id : "");
  const refreshInvoice = useRefreshInvoice(forInvoice ? target.invoice_id : "");
  const refresh = forInvoice ? refreshInvoice : refreshOrder;
  const keys = useIdempotencyKeys();
  const [method, setMethod] = useState<RecordMethod>("cash");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  // null is now; a day is money received earlier (cash handed in late, a cheque banked)
  const [paidOn, setPaidOn] = useState<Date | null>(null);
  const format = useCompanyFormat();
  const [problem, setProblem] = useState<{ amount?: string; reference?: string }>({});
  const referenceLabel = RECORD_METHOD[method].reference;
  const record = useMutation({
    mutationFn: () => {
      // midday in the company's zone, so the day is the same wherever staff sit; today's is now
      const midday = paidOn ? zonedInstant(paidOn, "12:00", format.timezone) : undefined;
      // number-ok: comparing instants, not money
      const paidAt = midday && Date.parse(midday) < Date.now() ? midday : undefined;
      const body = {
        ...target,
        method,
        amount: amount.trim(),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(paidAt ? { paid_at: paidAt } : {}),
      };
      return paymentActions.record(body, keys.keyFor(body));
    },
    onSuccess: async () => {
      keys.settled();
      await refresh();
      onClose();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = {
      amount: /^\d{1,12}(\.\d{1,2})?$/.test(amount.trim()) && /[1-9]/.test(amount) ? undefined : "An amount, e.g. 6500",
      reference: reference.trim() ? undefined : `The ${referenceLabel.toLowerCase()}`,
    };
    setProblem(next);
    if (!next.amount && !next.reference) record.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`Record a payment for ${number}`}
        description={
          forInvoice
            ? "Money paid outside the app, such as a bank transfer to the account on the invoice. Up to what is still owed; it is posted to the books at once."
            : "Money the customer paid outside the app. Up to what is still owed; it is posted to the books at once."
        }
      >
        <form onSubmit={submit} className={styles.form}>
          <Select
            label="How it was paid"
            value={method}
            onValueChange={(v) => setMethod(v as RecordMethod)}
            options={(Object.keys(RECORD_METHOD) as RecordMethod[]).map((m) => ({
              value: m,
              label: RECORD_METHOD[m].label,
            }))}
          />
          <Input
            label="Amount (KES)"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={problem.amount}
          />
          <Input
            label={referenceLabel}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            error={problem.reference}
            description={
              method === "mpesa" ? "Money paid to the till is in Payments → Till: match it there instead." : undefined
            }
          />
          <SegmentedControl
            label="Paid"
            value={paidOn ? "day" : "today"}
            onValueChange={(v) => setPaidOn(v === "today" ? null : new Date(Date.now() - 86_400_000))}
            options={[
              { value: "today", label: "Today" },
              { value: "day", label: "On an earlier day" },
            ]}
          />
          {paidOn ? (
            <DatePicker label="Paid on" value={paidOn} onChange={(d) => setPaidOn(d ?? null)} maxDate={new Date()} />
          ) : null}
          <Textarea label="Notes (optional)" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          {record.error ? (
            <Alert tone="danger" title="Not recorded">
              {messageFor(errorOf(record.error))}
            </Alert>
          ) : null}
          <Footer onClose={onClose}>
            <Button type="submit" loading={record.isPending}>
              Record payment
            </Button>
          </Footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Prompt the customer's phone to pay what is owed by M-Pesa; the payment shows as it settles. */
export function SendStkDialog({
  orderId,
  orderNumber,
  phone,
  onClose,
}: {
  orderId: string;
  orderNumber: string;
  phone: string | null;
  onClose: () => void;
}) {
  const refresh = useRefreshOrder(orderId);
  const keys = useIdempotencyKeys();
  const [number, setNumber] = useState(phone ?? "");
  const send = useMutation({
    mutationFn: () => {
      const body = { order_id: orderId, phone_number: number.trim() };
      return paymentActions.stk(body, keys.keyFor(body));
    },
    onSuccess: async () => {
      keys.settled();
      await refresh();
    },
  });
  if (send.data)
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          title="Request sent"
          description={`The phone ${number.trim()} is asked to pay KES ${send.data.amount}. The payment shows under Payments, and the order is marked paid when the customer approves it.`}
        >
          <div className={styles.footer}>
            <Button onClick={onClose}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`Send an M-Pesa request for ${orderNumber}`}
        description="The customer's phone asks them to pay what is still owed, rounded up to whole shillings."
      >
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            send.mutate();
          }}
        >
          <Input
            label="M-Pesa number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="+254712345678"
            description="A Kenyan mobile number, starting +254."
          />
          {send.error ? (
            <Alert tone="danger" title="Not sent">
              {messageFor(errorOf(send.error))}
            </Alert>
          ) : null}
          <Footer onClose={onClose}>
            <Button type="submit" disabled={!number.trim()} loading={send.isPending}>
              Send request
            </Button>
          </Footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Let the order go out before it is paid; it stays on Orders → Postpaid, to collect until it is paid. */
export function PostpaidDialog({
  orderId,
  orderNumber,
  onClose,
}: {
  orderId: string;
  orderNumber: string;
  onClose: () => void;
}) {
  const refresh = useRefreshOrder(orderId);
  const [reason, setReason] = useState("");
  const [missing, setMissing] = useState<string>();
  const postpaid = useMutation({
    mutationFn: () =>
      ok(
        api.POST("/v1/admin/orders/{id}/postpaid", {
          params: { path: { id: orderId } },
          body: { reason: reason.trim() },
        }),
      ),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!reason.trim()) return setMissing("Say why: the team follows up with it.");
    setMissing(undefined);
    postpaid.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`Deliver ${orderNumber} before payment?`}
        description="Dispatch can plan it now, unpaid. Unless the customer is on credit terms, an invoice is issued and sent to them now, and the order is paid when the invoice is. It stays under Orders → Postpaid until then."
      >
        <form onSubmit={submit} className={styles.form}>
          <Textarea
            label="Reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Regular customer, pays on Friday"
            error={missing}
          />
          {postpaid.error ? (
            <Alert tone="danger" title="Not changed">
              {messageFor(errorOf(postpaid.error))}
            </Alert>
          ) : null}
          <Footer onClose={onClose}>
            <Button type="submit" loading={postpaid.isPending}>
              Deliver before payment
            </Button>
          </Footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A refunded order is owed again: it stays on Orders → To collect until it is paid. */
export function MarkUnpaidDialog({
  orderId,
  orderNumber,
  onClose,
}: {
  orderId: string;
  orderNumber: string;
  onClose: () => void;
}) {
  const refresh = useRefreshOrder(orderId);
  const [reason, setReason] = useState("");
  const [missing, setMissing] = useState<string>();
  const mark = useMutation({
    mutationFn: () =>
      ok(
        api.POST("/v1/admin/orders/{id}/mark-unpaid", {
          params: { path: { id: orderId } },
          body: { reason: reason.trim() },
        }),
      ),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!reason.trim()) return setMissing("Say why: the team follows up with it.");
    setMissing(undefined);
    mark.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`Mark ${orderNumber} unpaid?`}
        description="It shows as Unpaid again, owing what it did before the refund, and stays under Orders → To collect until it is paid."
      >
        <form onSubmit={submit} className={styles.form}>
          <Textarea
            label="Reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Refunded in error; the customer will pay"
            error={missing}
          />
          {mark.error ? (
            <Alert tone="danger" title="Not changed">
              {messageFor(errorOf(mark.error))}
            </Alert>
          ) : null}
          <Footer onClose={onClose}>
            <Button type="submit" loading={mark.isPending}>
              Mark unpaid
            </Button>
          </Footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}
